import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CampaignSetup from './components/CampaignSetup';
import EmailComposer from './components/EmailComposer';
import RecipientManager from './components/RecipientManager';
import CampaignPreview from './components/CampaignPreview';
import CampaignAnalytics from './components/CampaignAnalytics';

const EmailCampaignManager = () => {
  const [activeTab, setActiveTab] = useState('compose');
  const [campaignData, setCampaignData] = useState({});
  const [emailContent, setEmailContent] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  const tabs = [
    { id: 'compose', label: 'Compose Campaign', icon: 'Edit3' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' }
  ];

  const handleCampaignUpdate = (data) => {
    setCampaignData(data);
  };

  const handleContentUpdate = (content) => {
    setEmailContent(content);
  };

  const handleRecipientUpdate = (recipients) => {
    setSelectedRecipients(recipients);
  };

  const handleSendCampaign = () => {
    // Mock campaign send functionality
    alert(`Campaign "${campaignData?.name || 'Untitled Campaign'}" sent to ${selectedRecipients?.length} recipients!`);
  };

  const canSendCampaign = () => {
    return campaignData?.name && 
           campaignData?.subject && 
           campaignData?.mediaList && 
           emailContent?.trim()?.length > 0 && 
           selectedRecipients?.length > 0;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                Email Campaign Manager
              </h1>
              <p className="text-muted-foreground">
                Create, manage, and track personalized media outreach campaigns
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button 
                variant="outline" 
                onClick={() => setIsPreviewMode(!isPreviewMode)}
                iconName={isPreviewMode ? "Edit3" : "Eye"} 
                iconPosition="left"
              >
                {isPreviewMode ? 'Edit Mode' : 'Preview Mode'}
              </Button>
              {activeTab === 'compose' && (
                <Button 
                  variant="default"
                  onClick={handleSendCampaign}
                  disabled={!canSendCampaign()}
                  iconName="Send" 
                  iconPosition="left"
                >
                  Send Campaign
                </Button>
              )}
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex space-x-1 mb-6 border-b border-border">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'compose' && (
            <>
              {isPreviewMode ? (
                /* Preview Mode - Full Width */
                (<div className="space-y-6">
                  <CampaignPreview 
                    campaignData={campaignData}
                    emailContent={emailContent}
                  />
                </div>)
              ) : (
                /* Compose Mode - Three Column Layout */
                (<div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                  {/* Left Panel - Campaign Setup */}
                  <div className="lg:col-span-3">
                    <CampaignSetup onCampaignUpdate={handleCampaignUpdate} />
                  </div>
                  {/* Center Panel - Email Composer */}
                  <div className="lg:col-span-6">
                    <EmailComposer onContentUpdate={handleContentUpdate} />
                  </div>
                  {/* Right Panel - Recipient Manager */}
                  <div className="lg:col-span-3">
                    <RecipientManager onRecipientUpdate={handleRecipientUpdate} />
                  </div>
                </div>)
              )}

              {/* Campaign Status Bar */}
              {!isPreviewMode && (
                <div className="mt-6 bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <Icon 
                          name={campaignData?.name ? "CheckCircle" : "Circle"} 
                          size={16} 
                          className={campaignData?.name ? "text-success" : "text-muted-foreground"} 
                        />
                        <span className="text-sm text-muted-foreground">Campaign Details</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Icon 
                          name={emailContent?.trim() ? "CheckCircle" : "Circle"} 
                          size={16} 
                          className={emailContent?.trim() ? "text-success" : "text-muted-foreground"} 
                        />
                        <span className="text-sm text-muted-foreground">Email Content</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Icon 
                          name={selectedRecipients?.length > 0 ? "CheckCircle" : "Circle"} 
                          size={16} 
                          className={selectedRecipients?.length > 0 ? "text-success" : "text-muted-foreground"} 
                        />
                        <span className="text-sm text-muted-foreground">
                          Recipients ({selectedRecipients?.length})
                        </span>
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {canSendCampaign() ? (
                        <span className="text-success">Ready to send</span>
                      ) : (
                        <span>Complete all sections to send</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {activeTab === 'analytics' && (
            <CampaignAnalytics />
          )}
        </div>
      </main>
    </div>
  );
};

export default EmailCampaignManager;